import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import storage from "@src/shared/utils/localStorage";

export const ThemePreferenceValues = ["system", "light", "dark"] as const;

export type ThemePreference = (typeof ThemePreferenceValues)[number];

export type ThemeColorScheme = Exclude<ThemePreference, "system">;

interface ThemeStoreState {
  effectiveColorScheme: ThemeColorScheme;
  systemColorScheme: ThemeColorScheme;
  themePreference: ThemePreference;
}

interface ThemeStoreActions {
  setSystemColorScheme: (systemColorScheme: ThemeColorScheme) => void;
  setThemePreference: (themePreference: ThemePreference) => void;
}

export type ThemeStore = ThemeStoreState & ThemeStoreActions;

export function isThemePreference(value: unknown): value is ThemePreference {
  return (
    typeof value === "string" &&
    ThemePreferenceValues.includes(value as ThemePreference)
  );
}

export function getSystemColorScheme(): ThemeColorScheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function resolveThemeColorScheme(
  themePreference: ThemePreference,
  systemColorScheme: ThemeColorScheme,
): ThemeColorScheme {
  if (themePreference === "system") return systemColorScheme;

  return themePreference;
}

export function applyThemeColorScheme(colorScheme: ThemeColorScheme): void {
  if (typeof document === "undefined") return;

  document.documentElement.classList.toggle("dark", colorScheme === "dark");
  document.documentElement.style.colorScheme = colorScheme;
}

const initialSystemColorScheme = getSystemColorScheme();

export const themeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      effectiveColorScheme: resolveThemeColorScheme(
        "system",
        initialSystemColorScheme,
      ),
      systemColorScheme: initialSystemColorScheme,
      themePreference: "system",
      setSystemColorScheme: (systemColorScheme) => {
        set((state) => ({
          effectiveColorScheme: resolveThemeColorScheme(
            state.themePreference,
            systemColorScheme,
          ),
          systemColorScheme,
        }));
      },
      setThemePreference: (themePreference) => {
        set((state) => ({
          effectiveColorScheme: resolveThemeColorScheme(
            themePreference,
            state.systemColorScheme,
          ),
          themePreference,
        }));
      },
    }),
    {
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<ThemeStore> | undefined;
        const themePreference = isThemePreference(persisted?.themePreference)
          ? persisted.themePreference
          : "system";
        const systemColorScheme = getSystemColorScheme();
        const effectiveColorScheme = resolveThemeColorScheme(
          themePreference,
          systemColorScheme,
        );

        return {
          ...currentState,
          effectiveColorScheme,
          systemColorScheme,
          themePreference,
        };
      },
      name: "theme-store",
      partialize: (state) => ({ themePreference: state.themePreference }),
      storage: createJSONStorage(() => storage),
    },
  ),
);

export function initializeThemePreference(): void {
  const systemColorScheme = getSystemColorScheme();
  const storedThemePreference = themeStore.getState().themePreference;
  const themePreference = isThemePreference(storedThemePreference)
    ? storedThemePreference
    : "system";
  const effectiveColorScheme = resolveThemeColorScheme(
    themePreference,
    systemColorScheme,
  );

  themeStore.setState({
    effectiveColorScheme,
    systemColorScheme,
    themePreference,
  });
  applyThemeColorScheme(effectiveColorScheme);
}

export function useThemePreference() {
  return {
    effectiveColorScheme: themeStore((state) => state.effectiveColorScheme),
    setThemePreference: themeStore((state) => state.setThemePreference),
    systemColorScheme: themeStore((state) => state.systemColorScheme),
    themePreference: themeStore((state) => state.themePreference),
  };
}
